import type {
  TeamMember,
  Invitation,
} from 'src/hooks/use-team-management';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import EmailIcon from '@mui/icons-material/Email';
import CardContent from '@mui/material/CardContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OutlinedInput from '@mui/material/OutlinedInput';
import TableContainer from '@mui/material/TableContainer';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';

import {
  useInviteUser,
  useTeamMembers,
  useInvitations,
  useUpdateMember,
  useRemoveMember,
  useResendInvitation,
  useRevokeInvitation,
} from 'src/hooks/use-team-management';

import { useOrganization } from 'src/context/organization-context';

import { OrgSectionPicker } from 'src/components/settings/OrgSectionPicker';

// ----------------------------------------------------------------------

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/settings/organization/team')({
  component: OrganizationTeamPage,
});

function OrganizationTeamPage() {
  const { t } = useTranslation('settings');
  const { activeOrganization, canManageTeam, isOwner, accessibleClients } = useOrganization();
  const [tabValue, setTabValue] = useState(0);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editMemberDialogOpen, setEditMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMember, setMenuMember] = useState<TeamMember | null>(null);
  const [processingInvitationId, setProcessingInvitationId] = useState<string | null>(null);

  // Invitation form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'editor'>('editor');
  const [inviteHasAllAccess, setInviteHasAllAccess] = useState(false);
  const [inviteClientIds, setInviteClientIds] = useState<string[]>([]);

  // Edit member form state
  const [editRole, setEditRole] = useState<'owner' | 'editor'>('editor');
  const [editHasAllAccess, setEditHasAllAccess] = useState(false);
  const [editClientIds, setEditClientIds] = useState<string[]>([]);

  // Initialize edit form when selectedMember changes
  useEffect(() => {
    if (selectedMember) {
      setEditRole(selectedMember.role as 'owner' | 'editor');
      setEditHasAllAccess(selectedMember.hasAccessToAllClients);
      setEditClientIds([]);
    }
  }, [selectedMember]);

  // Queries
  const { data: members, isLoading: loadingMembers, error: membersError } = useTeamMembers();
  const { data: invitations, isLoading: loadingInvitations } = useInvitations();

  // Mutations
  const inviteUserMutation = useInviteUser();
  const updateMemberMutation = useUpdateMember();
  const removeMemberMutation = useRemoveMember();
  const resendInvitationMutation = useResendInvitation();
  const revokeInvitationMutation = useRevokeInvitation();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, member: TeamMember) => {
    setAnchorEl(event.currentTarget);
    setMenuMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuMember(null);
  };

  const handleEditMember = () => {
    setSelectedMember(menuMember);
    setEditMemberDialogOpen(true);
    handleMenuClose();
  };

  const handleEditMemberSubmit = async () => {
    if (!selectedMember) return;
    try {
      await updateMemberMutation.mutateAsync({
        userId: selectedMember.userId,
        params: {
          role: editRole,
          hasAccessToAllClients: editHasAllAccess,
          ...(!editHasAllAccess && editClientIds.length > 0 && { clientIds: editClientIds }),
        },
      });
      setEditMemberDialogOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error('Failed to update member:', error);
    }
  };

  const handleEditDialogClose = () => {
    setEditMemberDialogOpen(false);
    setSelectedMember(null);
  };

  const handleRemoveMember = async () => {
    if (!menuMember) return;
    try {
      await removeMemberMutation.mutateAsync(menuMember.userId);
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
    handleMenuClose();
  };

  const handleInviteSubmit = async () => {
    try {
      await inviteUserMutation.mutateAsync({
        email: inviteEmail,
        role: inviteRole,
        hasAccessToAllClients: inviteHasAllAccess,
        ...(!inviteHasAllAccess && inviteClientIds.length > 0 && { clientIds: inviteClientIds }),
      });
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('editor');
      setInviteHasAllAccess(false);
      setInviteClientIds([]);
    } catch (error) {
      console.error('Failed to send invitation:', error);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    setProcessingInvitationId(invitationId);
    try {
      await resendInvitationMutation.mutateAsync(invitationId);
    } catch (error) {
      console.error('Failed to resend invitation:', error);
    } finally {
      setProcessingInvitationId(null);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setProcessingInvitationId(invitationId);
    try {
      await revokeInvitationMutation.mutateAsync(invitationId);
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
    } finally {
      setProcessingInvitationId(null);
    }
  };

  const getMemberName = (member: TeamMember) => {
    if (member.user.firstName) {
      return `${member.user.firstName} ${member.user.lastName || ''}`.trim();
    }
    return member.user.userName;
  };

  const getInviterName = (invitation: Invitation) => {
    if (invitation.inviter.firstName) {
      return `${invitation.inviter.firstName} ${invitation.inviter.lastName || ''}`.trim();
    }
    return invitation.inviter.userName;
  };

  const pendingInvitations = (invitations as Invitation[] | undefined)?.filter((inv) => inv.status === 'pending') || [];

  if (!canManageTeam()) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t('team.title', 'Team Management')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('team.subtitle', 'Manage team members, roles, and permissions')}
          </Typography>
        </Box>
        <Alert severity="warning">
          {t('team.noAccess', 'Only organization owners can manage team members.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Organization section picker */}
      <OrgSectionPicker />

      {/* Page Header */}
      <Box sx={{ mt: 3, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('team.title', 'Team Management')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('team.subtitleOrg', 'Manage team members, roles, and permissions for {{orgName}}', { orgName: activeOrganization?.name })}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          {t('team.inviteMember', 'Invite Member')}
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label={t('team.tabs.members', 'Team Members ({{count}})', { count: members?.length || 0 })} />
          <Tab label={t('team.tabs.invitations', 'Pending Invitations ({{count}})', { count: pendingInvitations.length })} />
        </Tabs>
      </Box>

      {/* Team Members Tab */}
      {tabValue === 0 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loadingMembers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : membersError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {t('team.members.loadError', 'Failed to load team members')}
              </Alert>
            ) : members && members.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('team.members.colMember', 'Member')}</TableCell>
                      <TableCell>{t('team.members.colRole', 'Role')}</TableCell>
                      <TableCell>{t('team.members.colClientAccess', 'Client Access')}</TableCell>
                      <TableCell>{t('team.members.colJoined', 'Joined')}</TableCell>
                      <TableCell align="right">{t('team.members.colActions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar src={member.user.avatar || undefined}>
                              {getMemberName(member).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {getMemberName(member)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {member.user.userName}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={member.role}
                            size="small"
                            color={member.role === 'owner' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {member.hasAccessToAllClients ? (
                            <Chip label={t('team.members.allClients', 'All clients')} size="small" variant="outlined" />
                          ) : (
                            <Typography variant="body2">
                              {t('team.members.clientCount', { count: member.clientAccessCount, defaultValue: '{{count}} client(s)' })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(member.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, member)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('team.members.empty', 'No team members yet. Invite someone to get started!')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending Invitations Tab */}
      {tabValue === 1 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loadingInvitations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : pendingInvitations.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('team.invitations.colEmail', 'Email')}</TableCell>
                      <TableCell>{t('team.members.colRole', 'Role')}</TableCell>
                      <TableCell>{t('team.invitations.colInvitedBy', 'Invited By')}</TableCell>
                      <TableCell>{t('team.invitations.colExpires', 'Expires')}</TableCell>
                      <TableCell align="right">{t('team.members.colActions', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingInvitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{invitation.email}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.role}
                            size="small"
                            color={invitation.role === 'owner' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getInviterName(invitation)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(invitation.expiresAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => handleResendInvitation(invitation.id)}
                            disabled={processingInvitationId === invitation.id}
                          >
                            {processingInvitationId === invitation.id && resendInvitationMutation.isPending ? t('team.invitations.sending', 'Sending...') : t('team.invitations.resend', 'Resend')}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRevokeInvitation(invitation.id)}
                            disabled={processingInvitationId === invitation.id}
                          >
                            {processingInvitationId === invitation.id && revokeInvitationMutation.isPending ? t('team.invitations.revoking', 'Revoking...') : t('team.invitations.revoke', 'Revoke')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('team.invitations.empty', 'No pending invitations')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Member Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditMember}>{t('team.menu.editRoleAccess', 'Edit Role & Access')}</MenuItem>
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          {t('team.menu.removeFromTeam', 'Remove from Team')}
        </MenuItem>
      </Menu>

      {/* Invite Member Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('team.inviteDialog.title', 'Invite Team Member')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label={t('team.inviteDialog.emailLabel', 'Email Address')}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>{t('team.inviteDialog.roleLabel', 'Role')}</InputLabel>
              <Select
                value={inviteRole}
                label={t('team.inviteDialog.roleLabel', 'Role')}
                onChange={(e) => setInviteRole(e.target.value as 'owner' | 'editor')}
              >
                <MenuItem value="editor">{t('team.roles.editorOption', 'Editor - Can edit content')}</MenuItem>
                <MenuItem value="owner">{t('team.roles.ownerOption', 'Owner - Full control')}</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={inviteHasAllAccess}
                  onChange={(e) => setInviteHasAllAccess(e.target.checked)}
                />
              }
              label={t('team.inviteDialog.allClientsLabel', 'Grant access to all clients (including future ones)')}
            />
            {!inviteHasAllAccess && accessibleClients.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t('team.inviteDialog.clientAccessLabel', 'Client Access')}</InputLabel>
                <Select
                  multiple
                  value={inviteClientIds}
                  onChange={(e) => setInviteClientIds(e.target.value as string[])}
                  input={<OutlinedInput label={t('team.inviteDialog.clientAccessLabel', 'Client Access')} />}
                  renderValue={(selected) => {
                    const selectedNames = (selected as string[])
                      .map((id) => accessibleClients.find((c) => c.id === id)?.name)
                      .filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                >
                  {accessibleClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      <Checkbox checked={inviteClientIds.includes(client.id)} />
                      <ListItemText primary={client.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(inviteUserMutation as { error?: Error }).error && (
              <Alert severity="error">
                {(inviteUserMutation as { error?: Error }).error!.message}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleInviteSubmit}
            disabled={!inviteEmail || inviteUserMutation.isPending}
          >
            {inviteUserMutation.isPending ? t('team.invitations.sending', 'Sending...') : t('team.inviteDialog.sendInvitation', 'Send Invitation')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog
        open={editMemberDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('team.editDialog.title', 'Edit Team Member')}</DialogTitle>
        <DialogContent>
          {selectedMember && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar src={selectedMember.user.avatar || undefined}>
                  {getMemberName(selectedMember).charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {getMemberName(selectedMember)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedMember.user.userName}
                  </Typography>
                </Box>
              </Box>
              <FormControl fullWidth>
                <InputLabel>{t('team.inviteDialog.roleLabel', 'Role')}</InputLabel>
                <Select
                  value={editRole}
                  label={t('team.inviteDialog.roleLabel', 'Role')}
                  onChange={(e) => setEditRole(e.target.value as 'owner' | 'editor')}
                >
                  <MenuItem value="editor">{t('team.roles.editorOption', 'Editor - Can edit content')}</MenuItem>
                  <MenuItem value="owner">{t('team.roles.ownerOption', 'Owner - Full control')}</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={editHasAllAccess}
                    onChange={(e) => setEditHasAllAccess(e.target.checked)}
                  />
                }
                label={t('team.inviteDialog.allClientsLabel', 'Grant access to all clients (including future ones)')}
              />
              {!editHasAllAccess && accessibleClients.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>{t('team.inviteDialog.clientAccessLabel', 'Client Access')}</InputLabel>
                  <Select
                    multiple
                    value={editClientIds}
                    onChange={(e) => setEditClientIds(e.target.value as string[])}
                    input={<OutlinedInput label={t('team.inviteDialog.clientAccessLabel', 'Client Access')} />}
                    renderValue={(selected) => {
                      const selectedNames = (selected as string[])
                        .map((id) => accessibleClients.find((c) => c.id === id)?.name)
                        .filter(Boolean);
                      return selectedNames.join(', ');
                    }}
                  >
                    {accessibleClients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        <Checkbox checked={editClientIds.includes(client.id)} />
                        <ListItemText primary={client.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {updateMemberMutation.error ? (
                <Alert severity="error">
                  {t('team.editDialog.updateError', 'Failed to update member. Please try again.')}
                </Alert>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleEditMemberSubmit}
            disabled={updateMemberMutation.isPending}
          >
            {updateMemberMutation.isPending ? t('common:saving') : t('team.editDialog.saveChanges', 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
